import type { SemanticPortContext, SemanticPortResult } from './ports';

export type ComponentSemanticActionType =
  | 'reveal_coupon'
  | 'run_spin'
  | 'dismiss_experience'
  | 'select_variant';

export interface ComponentCapabilityInput {
  couponCode?: string;
  targetBlockId?: string;
  wheelId?: string;
  experienceId?: string;
  productId?: string;
  variantId?: string;
}

export type ComponentCapabilityHandler = (
  input: ComponentCapabilityInput,
  context: SemanticPortContext,
) => Promise<SemanticPortResult | void> | SemanticPortResult | void;

export class MissingSemanticComponentCapabilityError extends Error {
  constructor(
    readonly actionType: ComponentSemanticActionType,
    readonly targetId?: string,
  ) {
    super(
      `No ${actionType} component capability is registered${
        targetId ? ` for ${targetId}` : ''
      }`,
    );
    this.name = 'MissingSemanticComponentCapabilityError';
  }
}

/**
 * Provider-scoped component capability registry. Business handlers still enter
 * through the central dispatcher; reusable components register only the local
 * state mutation they own.
 */
export class SemanticComponentCapabilityRegistry {
  private readonly handlers = new Map<
    ComponentSemanticActionType,
    Map<string, ComponentCapabilityHandler>
  >();

  register(
    actionType: ComponentSemanticActionType,
    targetId: string,
    handler: ComponentCapabilityHandler,
  ): () => void {
    const byTarget = this.handlers.get(actionType) ?? new Map();
    byTarget.set(targetId, handler);
    this.handlers.set(actionType, byTarget);
    return () => {
      if (byTarget.get(targetId) === handler) {
        byTarget.delete(targetId);
      }
      if (byTarget.size === 0) this.handlers.delete(actionType);
    };
  }

  async execute(
    actionType: ComponentSemanticActionType,
    targetId: string | undefined,
    input: ComponentCapabilityInput,
    context: SemanticPortContext,
  ): Promise<SemanticPortResult | void> {
    const byTarget = this.handlers.get(actionType);
    const exact = targetId ? byTarget?.get(targetId) : undefined;
    const only =
      !targetId && byTarget?.size === 1
        ? [...byTarget.values()][0]
        : undefined;
    const handler = exact ?? only;
    if (!handler) {
      throw new MissingSemanticComponentCapabilityError(actionType, targetId);
    }
    return handler(input, context);
  }
}
